import {
    Avatar,
    Field,
    FluentProvider,
    makeStyles,
    mergeClasses,
    Tag,
    TagPicker,
    TagPickerControl,
    TagPickerGroup,
    TagPickerInput,
    TagPickerList,
    TagPickerOption,
} from '@fluentui/react-components';
import { memo, useMemo, useRef, useState } from 'react';
import { type Root } from 'react-dom/client';
import { fetchQuery, graphql, RelayEnvironmentProvider, useRelayEnvironment } from 'react-relay';
import { relayEnv } from '../relay/relay-env';
import type {
    PeoplePickerQuery,
    PeoplePickerQuery$data,
} from './__generated__/PeoplePickerQuery.graphql';
import { ThemeProvider, useThemeContext } from '../theme/ThemeContext';

interface IPeoplePickerProps {
    label: string;
    onChange: (value: TOptions) => void;
    principalType: 1 | 8;
    siteUrl?: string;
    translate: TranslateFn;
    value?: TOptions;
}

// type Xy = PeoplePickerQuery$data['getSharePointSiteMembers'][number][];
// type Xy = Required<NonNullable<NonNullable<PeoplePickerQuery$data['getSharePointSiteMembers']>[number]>>;
/*
type TJobs = jobsQueryFragment$data['content'][number][];
type TJob = TJobs[number];
 */

type TOptionKeys = keyof NonNullable<
    NonNullable<PeoplePickerQuery$data['getSharePointSiteMembers']>[number]
>;
export type TPeoplePickerOption = Record<TOptionKeys, string>;
type TOptions = TPeoplePickerOption[];

const PeoplePicker = memo<IPeoplePickerProps>((props) => {
    const [input, setInput] = useState<string>('');
    const [open, setOpen] = useState<boolean>(false);
    const [options, setOptions] = useState<Map<string, TPeoplePickerOption>>();
    const [loading, setLoading] = useState<boolean>(false);
    const loadingRef = useRef(loading);
    const relayEnv = useRelayEnvironment();
    const styles = usePickerStyles();
    const { label, principalType, siteUrl, translate } = props;
    const disabled = loading || !siteUrl;
    const { theme } = useThemeContext();

    const selectedOptions = useMemo<string[]>(() => {
        if (props.value) {
            return props.value.map((entry) => entry.loginName);
        }
        return [];
    }, [props.value]);

    const pickerOptions = useMemo(() => {
        if (options && options.size > 0) {
            return Array.from(options.entries())
                .filter((entry) => !selectedOptions.includes(entry[0]))
                .map(([key, option]) => (
                    <TagPickerOption
                        key={key}
                        media={
                            <Avatar
                                shape="square"
                                aria-hidden
                                name={option.name}
                                color="colorful"
                            />
                        }
                        text={option.name}
                        value={option.loginName}
                    >
                        <div className={styles.tagPrimaryText}>{option.name}</div>
                    </TagPickerOption>
                ));
        }

        return [];
    }, [selectedOptions, options]);

    return (
        <FluentProvider theme={theme} applyStylesToPortals={true} className={styles.provider}>
            <Field
                hint={
                    loading
                        ? {
                              className: 'bio-properties-panel-description',
                              children: translate('Loading...'),
                          }
                        : 'Drücken Sie ENTER, um die Suche zu starten (mindestens 3 Zeichen eingeben)'
                }
                label={{
                    children: label,
                    className: 'bio-properties-panel-label',
                }}
            >
                <TagPicker
                    disabled={disabled}
                    noPopover={
                        disabled /*|| !options || options.size < 1*/ /*|| pickerOptions.length < 1*/
                    }
                    onOpenChange={(event: any, data) => {
                        if (
                            event.type === 'keydown' &&
                            event.key === 'Enter' &&
                            input.length > 2 &&
                            !options &&
                            !loadingRef.current
                        ) {
                            const inputEl: HTMLInputElement = event.currentTarget;
                            setLoading(true);
                            loadingRef.current = true;
                            setOpen(false);
                            // setTimeout(() => {
                            fetchQuery<PeoplePickerQuery>(
                                relayEnv,
                                query,
                                {
                                    input: {
                                        queryString: input,
                                        siteUrl: siteUrl!,
                                        principalType,
                                    },
                                },
                                { fetchPolicy: 'store-or-network' }
                            )
                                .toPromise()
                                .then((queryResult) => {
                                    const members = queryResult?.getSharePointSiteMembers?.filter(
                                        (entry) => !!entry
                                    );
                                    setOptions(
                                        Array.isArray(members) && members.length > 0
                                            ? new Map(
                                                  members.map(
                                                      (entry) =>
                                                          [entry.loginName, entry] as [
                                                              string,
                                                              TPeoplePickerOption,
                                                          ]
                                                  )
                                              )
                                            : undefined
                                    );
                                    loadingRef.current = false;
                                    setLoading(false);
                                    setOpen(true);
                                })
                                .catch(() => {
                                    loadingRef.current = false;
                                    setLoading(false);
                                    setOpen(true);
                                })
                                .finally(() => {
                                    setTimeout(() => {
                                        inputEl.focus();
                                    }, 25);
                                });
                            // }, 1000);
                        } else if (!loadingRef.current) {
                            setOpen(data.open);
                        }
                    }}
                    onOptionSelect={(_event, data) => {
                        if (data.value === 'no-matches') {
                            return;
                        }

                        const allOptions = new Map(
                            [...(props.value || []), ...Array.from(options?.values() || [])].map(
                                (entry) => [entry.loginName, entry]
                            )
                        );

                        setInput('');
                        setOptions(undefined);
                        props.onChange(data.selectedOptions.map((val) => allOptions.get(val)!));
                    }}
                    open={disabled ? false : open}
                    selectedOptions={selectedOptions}
                >
                    <TagPickerControl
                        // className={styles.pickerCtrl}
                        className={mergeClasses(styles.pickerCtrl, 'bio-properties-panel-input')}
                        // expandIcon={{ className: styles.expandIcon }}
                    >
                        <TagPickerGroup
                            aria-label={translate('Selected {items}', { items: label })}
                            className={styles.pickerGroup}
                        >
                            {(props.value || []).map((entry) => (
                                <Tag
                                    key={entry.loginName}
                                    shape="rounded"
                                    media={
                                        <Avatar aria-hidden name={entry.name} color="colorful" />
                                    }
                                    primaryText={{ className: styles.tagPrimaryText }}
                                    value={entry.loginName}
                                >
                                    {entry.name}
                                </Tag>
                            ))}
                        </TagPickerGroup>

                        <TagPickerInput
                            aria-label={translate('Select {item}', { item: label })}
                            className={styles.pickerInput}
                            value={input}
                            onChange={(event) => {
                                setInput(event.target.value.trim());
                                setOptions(undefined);
                            }}
                        />
                    </TagPickerControl>

                    <TagPickerList>
                        {pickerOptions.length > 0 ? (
                            pickerOptions
                        ) : (
                            <TagPickerOption value="no-matches">
                                {translate(loading ? 'Loading...' : 'No selection available')}
                            </TagPickerOption>
                        )}
                    </TagPickerList>
                </TagPicker>
            </Field>
        </FluentProvider>
    );
});

const usePickerStyles = makeStyles({
    /*expandIcon: {
        minHeight: '25px',
        '>svg': {
            '>path': {
                stroke: 'black',
                strokeWidth: '0.11em',
            },
        },
    },*/
    provider: { backgroundColor: 'transparent' },
    pickerCtrl: {
        minHeight: 'unset',
        minWidth: 'unset',
        '::after': {
            display: 'none',
        },
        '&:hover': {
            'border-color': 'var(--input-border-color)',
        },
        '>span': {
            display: 'none',
            // minHeight: 'unset',
            // right: '4px',
        },
        /*'& .fui-TagPickerControl__expandIcon': {
            minHeight: '28px'
        }*/
    },
    pickerGroup: {
        padding: '4px 0',
        // paddingRight: '15px',
        '& >button': {
            //backgroundColor: tokens.colorNeutralBackground1,
            fontSize: 'smaller',
            height: 'unset',
            paddingLeft: '3px',
        },
    },
    pickerInput: {
        padding: 'unset',
    },
    tagPrimaryText: {
        whiteSpace: 'nowrap',
        overflowX: 'hidden',
        textOverflow: 'ellipsis',
    },
});

const query = graphql`
    query PeoplePickerQuery($input: SharePointPeoplePickerInput!) {
        getSharePointSiteMembers(input: $input) {
            # id id can not be used. relay throws an error because the id is not unique and conflicts with an existing process definition entry in the relay store
            memberId: id
            name: title
            email
            loginName
        }
    }
`;

export const renderPeoplePicker = (root: Root, props: IPeoplePickerProps) => {
    root.render(
        <RelayEnvironmentProvider environment={relayEnv}>
            <ThemeProvider>
                <PeoplePicker {...props} />
            </ThemeProvider>
        </RelayEnvironmentProvider>
    );
};
